import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/ToastManager';
import { ROUTES, NAVIGATION_OPTIONS } from '../config/routes';
import  styles  from '../styles/components/EmailChangeForm.styles.js';

/**
 * メールアドレス変更ページコンポーネント
 * 新しいメールアドレスの入力 → 認証コード送信 → 認証コード確認 → メールアドレス変更完了
 */
const EmailChangeForm = () => {
  const navigate = useNavigate();
  const { currentUser, memberData, getCsrfTokenFromCookie, logout } = useAuth();
  const { showSuccess, showError } = useToast();

  // ステップ定義
  const STEPS = {
    EMAIL_INPUT: 'EMAIL_INPUT',
    CODE_VERIFICATION: 'CODE_VERIFICATION',
    SUCCESS: 'SUCCESS'
  };

  // ステート管理
  const [currentStep, setCurrentStep] = useState(STEPS.EMAIL_INPUT);
  const [formData, setFormData] = useState({
    newEmail: '',
    newEmailVerify: '',
    verificationCode: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // ACTIVE状態チェック
  useEffect(() => {
    if (memberData && memberData.status !== 'ACTIVE') {
      showError('メールアドレス変更はACTIVE状態でのみ可能です');
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [memberData, navigate, showError]);


  /**
   * バリデーション
   */
  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return 'メールアドレスを入力してください';
    }
    const emailRegex = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailRegex.test(email)) {
      return '有効なメールアドレスを入力してください';
    }
    return '';
  };

  const validateVerificationCode = (code) => {
    if (!code || code.trim() === '') {
      return '認証コードを入力してください';
    }
    if (!/^\d{6}$/.test(code)) {
      return '認証コードは6桁の数字で入力してください';
    }
    return '';
  };

  /**
   * 入力変更ハンドラー
   */
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: name === 'verificationCode' ? value.replace(/\D/g, '').slice(0, 6) : value.toLowerCase().trim()
    }));

    // エラーをクリア
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  }, []);

  /**
   * メールアドレス変更申請（認証コード送信）
   */
  const handleRequestEmailChange = async (e) => {
    e.preventDefault();

    // バリデーション
    const newEmailError = validateEmail(formData.newEmail);
    const newEmailVerifyError = validateEmail(formData.newEmailVerify);

    if (newEmailError || newEmailVerifyError) {
      setErrors({
        newEmail: newEmailError,
        newEmailVerify: newEmailVerifyError
      });
      return;
    }

    if (formData.newEmail !== formData.newEmailVerify) {
      setErrors({
        newEmailVerify: 'メールアドレスが一致しません'
      });
      return;
    }

    if (formData.newEmail === currentUser?.email) {
      setErrors({
        newEmail: '現在のメールアドレスと同じです'
      });
      return;
    }

    setIsLoading(true);

    try {
      // CSRFトークンを取得
      const csrfToken = await getCsrfTokenFromCookie(true);
      
      if (!csrfToken) {
        throw new Error('セキュリティトークンの取得に失敗しました');
      }

      const response = await fetch('/api/me/email/request', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        body: JSON.stringify({
          newEmail: formData.newEmail,
          newEmailVerify: formData.newEmailVerify
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'メールアドレス変更申請に失敗しました');
      }

      showSuccess('認証コードを送信しました');
      setCurrentStep(STEPS.CODE_VERIFICATION);

    } catch (error) {
      console.error('[EMAIL CHANGE] Request error:', error);
      showError(error.message || '認証コード送信に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 認証コード確認（メールアドレス変更完了）
   */
  const handleVerifyCode = async (e) => {
    e.preventDefault();

    // バリデーション
    const codeError = validateVerificationCode(formData.verificationCode);
    if (codeError) {
      setErrors({ verificationCode: codeError });
      return;
    }

    setIsLoading(true);

    try {
     // CSRFトークンを取得
      const csrfToken = await getCsrfTokenFromCookie(true);
      
      if (!csrfToken) {
        throw new Error('セキュリティトークンの取得に失敗しました');
      }

      const response = await fetch(`/api/me/email/verify?code=${encodeURIComponent(formData.verificationCode)}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': csrfToken
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || '認証コードの確認に失敗しました');
      }

      showSuccess('メールアドレスを変更しました');
      setCurrentStep(STEPS.SUCCESS);

    } catch (error) {
      console.error('[EMAIL CHANGE] Verify error:', error);
      showError(error.message || '認証コードが正しくありません');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 認証コード再送信
   */
  const handleResendCode = async () => {
    setIsLoading(true);

    try {
      // CSRFトークンを取得
      const csrfToken = await getCsrfTokenFromCookie(true);
      
      if (!csrfToken) {
        throw new Error('セキュリティトークンの取得に失敗しました');
      }

      const response = await fetch('/api/me/email/request', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        body: JSON.stringify({
          newEmail: formData.newEmail,
          newEmailVerify: formData.newEmailVerify
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || '認証コード再送信に失敗しました');
      }

      showSuccess('認証コードを再送信しました');

    } catch (error) {
      console.error('[EMAIL CHANGE] Resend error:', error);
      showError(error.message || '認証コード再送信に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * キャンセルしてホームに戻る
   */
  const handleBackToHome = () => {
    setTimeout(() => {
      navigate(ROUTES.HOME);
    }, 100);
  };

  /**
   * ログアウト
   */
  const handleLogout = async () => {
    try {
      if (logout) {
        await logout();
        navigate(ROUTES.LOGIN, NAVIGATION_OPTIONS.REPLACE);
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  /**
   * ホバーエフェクト
   */
  const handleBackButtonHover = (e, isHover) => {
    e.target.style.backgroundColor = isHover ? '#002348' : '#09529f';
  };

  const handleSecondaryButtonHover = (e, isHover) => {
    e.target.style.backgroundColor = isHover ? '#002348' : '#6c757d';
  };

  const handlePrimaryButtonHover = (e, isHover) => {
    if (!isLoading) {
      e.target.style.backgroundColor = isHover ? '#002348' : '#007bff';
    }
  };

  return (
    <>
      <div style={styles.container}>
        <div style={styles.content}>
          {/* ヘッダー - 最初のステップのみ表示 */}
          {currentStep === STEPS.EMAIL_INPUT && (
            <div style={styles.header}>
              <h1 style={styles.headerTitle}>メールアドレス変更</h1>
              <button
                type="button"
                style={styles.backButton}
                onMouseOver={(e) => handleBackButtonHover(e, true)}
                onMouseOut={(e) => handleBackButtonHover(e, false)}
                onClick={handleBackToHome}
                disabled={isLoading}
              >
                ← ホームに戻る
              </button>
            </div>
          )}

          {/* その他のステップではシンプルなタイトル */}
          {currentStep !== STEPS.EMAIL_INPUT && (
            <h2 style={styles.title}>
              {currentStep === STEPS.CODE_VERIFICATION ? '🔐 認証コード確認'
                : currentStep === STEPS.SUCCESS ? '✅ 変更完了'
                : ''}
            </h2>
          )}

          {/* プログレスバー */}
          <div style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                width: currentStep === STEPS.EMAIL_INPUT ? '33%' : 
                       currentStep === STEPS.CODE_VERIFICATION ? '66%' : '100%'
              }}
            />
          </div>

          {/* 現在のメールアドレス表示 */}
          {currentStep !== STEPS.SUCCESS && (
            <div style={styles.currentEmailDisplay}>
              <p style={styles.label}>現在のメールアドレス</p>
              <p style={styles.currentEmail}>{currentUser?.email}</p>
            </div>
          )}

          {/* メールアドレス入力ステップ */}
          {currentStep === STEPS.EMAIL_INPUT && (
            <form onSubmit={handleRequestEmailChange}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <span>新しいメールアドレス</span><span style={styles.required}> *</span>
                </label>
                <input
                  type="email"
                  name="newEmail"
                  value={formData.newEmail}
                  onChange={handleInputChange}
                  placeholder="new-email@example.com"
                  style={{
                    ...styles.input,
                    ...(errors.newEmail && styles.inputError)
                  }}
                  disabled={isLoading}
                />
                {errors.newEmail && (
                  <div style={styles.errorText}>{errors.newEmail}</div>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <span>新しいメールアドレス（確認用）</span><span style={styles.required}> *</span>
                </label>
                <input
                  type="email"
                  name="newEmailVerify"
                  value={formData.newEmailVerify}
                  onChange={handleInputChange}
                  placeholder="new-email@example.com"
                  style={{
                    ...styles.input,
                    ...(errors.newEmailVerify && styles.inputError)
                  }}
                  disabled={isLoading}
                />
                {errors.newEmailVerify && (
                  <div style={styles.errorText}>{errors.newEmailVerify}</div>
                )}
              </div>

              <div style={styles.infoBox}>
                <p style={styles.infoText}>
                   <span>新しいメールアドレス宛に認証コードを送信します。</span><br />
                  <span>受信したコードで認証を完了すると、メールアドレスが変更されます。</span>
                </p>
              </div>

              <div style={styles.buttonGroup}>
                <button
                  type="submit"
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    ...(isLoading && styles.buttonDisabled)
                  }}
                  onMouseOver={(e) => handlePrimaryButtonHover(e, true)}
                  onMouseOut={(e) => handlePrimaryButtonHover(e, false)}
                  disabled={isLoading}
                >
                  {isLoading ? '送信中...' : '認証コードを送信'}
                </button>

                <button
                  type="button"
                  onClick={handleBackToHome}
                  style={{
                    ...styles.button,
                    ...styles.secondaryButton
                  }}
                  onMouseOver={(e) => handleSecondaryButtonHover(e, true)}
                  onMouseOut={(e) => handleSecondaryButtonHover(e, false)}
                  disabled={isLoading}
                >
                  キャンセル
                </button>
              </div>
            </form>
          )}

          {/* 認証コード確認ステップ */}
          {currentStep === STEPS.CODE_VERIFICATION && (
            <form onSubmit={handleVerifyCode}>
              <div style={styles.sentEmailDisplay}>
                <p style={styles.label}>認証コード送信先</p>
                <p style={styles.sentEmail}>{formData.newEmail}</p>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  <span>認証コード（6桁）</span><span style={styles.required}> *</span>
                </label>
                <input
                  type="text"
                  name="verificationCode"
                  value={formData.verificationCode}
                  onChange={handleInputChange}
                  placeholder="000000"
                  maxLength={6}
                  style={{
                    ...styles.input,
                    ...styles.codeInput,
                    ...(errors.verificationCode && styles.inputError)
                  }}
                  disabled={isLoading}
                />
                {errors.verificationCode && (
                  <div style={styles.errorText}>{errors.verificationCode}</div>
                )}
              </div>

              <div style={styles.infoBox}>
                <p style={styles.infoText}>
                  📬 メールが届かない場合は、迷惑メールフォルダをご確認ください。
                </p>
              </div>

              <div style={styles.buttonGroup}>
                <button
                  type="submit"
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    ...(isLoading && styles.buttonDisabled)
                  }}
                  onMouseOver={(e) => handlePrimaryButtonHover(e, true)}
                  onMouseOut={(e) => handlePrimaryButtonHover(e, false)}
                  disabled={isLoading}
                >
                  {isLoading ? '確認中...' : '認証してメールアドレスを変更'}
                </button>

                <button
                  type="button"
                  onClick={handleResendCode}
                  style={{
                    ...styles.button,
                    ...styles.secondaryButton
                  }}
                  onMouseOver={(e) => handleSecondaryButtonHover(e, true)}
                  onMouseOut={(e) => handleSecondaryButtonHover(e, false)}
                  disabled={isLoading}
                >
                  認証コードを再送信
                </button>

                <button
                  type="button"
                  onClick={handleBackToHome}
                  style={{
                    ...styles.button,
                    ...styles.tertiaryButton
                  }}
                  disabled={isLoading}
                >
                  キャンセル
                </button>
              </div>
            </form>
          )}

          {/* 成功ステップ */}
          {currentStep === STEPS.SUCCESS && (
            <div style={styles.successContainer}>
              <div style={styles.successIcon}>✓</div>
              <h3 style={styles.successTitle}>メールアドレスの変更が完了しました</h3>
              
              <div style={styles.emailChangeInfo}>
                <div style={styles.emailChangeRow}>
                  <span style={styles.emailChangeLabel}>変更前:</span>
                  <span style={styles.emailChangeValue}>{currentUser?.email}</span>
                </div>
                <div style={styles.emailChangeArrow}>↓</div>
                <div style={styles.emailChangeRow}>
                  <span style={styles.emailChangeLabel}>変更後:</span>
                  <span style={{...styles.emailChangeValue, color: '#28a745', fontWeight: 'bold'}}>
                    {formData.newEmail}
                  </span>
                </div>
              </div>

              <div style={styles.warningBox}>
                <p style={styles.warningText}>
                  <span>⚠️ メールアドレスを変更しました。</span><br />
                  <span>次回ログイン時は新しいメールアドレスをご利用ください。</span>
                </p>
              </div>

              <div style={styles.buttonGroup}>
                <button
                  onClick={handleLogout}
                  style={{
                    ...styles.button,
                    ...styles.primaryButton
                  }}
                  onMouseOver={(e) => handlePrimaryButtonHover(e, true)}
                  onMouseOut={(e) => handlePrimaryButtonHover(e, false)}
                >
                  ログアウトして再ログイン
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmailChangeForm;